<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
    <!-- État non connecté -->
    <div v-if="!isAuthenticated" class="space-y-12 max-w-6xl mx-auto">
      <div class="text-center space-y-4">
        <h1 class="text-4xl font-bold text-gray-900">Bienvenue sur Zebunet</h1>
        <p class="text-xl text-gray-600 max-w-3xl mx-auto">
          Découvrez notre plateforme de financement solidaire.
        </p>
      </div>

      <!-- Bannière de connexion -->
      <div class="bg-white rounded-2xl shadow-lg p-8">
        <div class="flex flex-col sm:flex-row items-center justify-between space-y-6 sm:space-y-0">
          <div class="text-center sm:text-left">
            <h3 class="text-2xl font-semibold text-zebu-orange">Déjà membre ?</h3>
            <p class="text-lg text-gray-600 mt-2">Connectez-vous pour accéder à votre espace personnel</p>
          </div>
          <div class="flex flex-col sm:flex-row gap-4">
            <router-link
              to="/register"
              class="inline-flex items-center justify-center px-8 py-3 text-lg font-medium rounded-xl border-2 border-zebu-orange text-zebu-orange hover:bg-zebu-orange hover:text-white transition-colors duration-300"
            >
              Créer un compte
            </router-link>
            <router-link
              to="/login"
              class="inline-flex items-center justify-center px-8 py-3 text-lg font-medium rounded-xl shadow-sm text-white bg-zebu-orange hover:bg-zebu-orange/90 transition-colors duration-300"
            >
              Se connecter
            </router-link>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Carte Adhésion -->
        <div class="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
          <div class="p-8 flex flex-col h-full">
            <div class="w-16 h-16 bg-zebu-orange/10 rounded-xl flex items-center justify-center mb-6">
              <svg class="w-8 h-8 text-zebu-orange" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div class="flex-grow">
              <h3 class="text-2xl font-semibold text-gray-900 mb-4">Devenir membre</h3>
              <p class="text-lg text-gray-600">Rejoignez notre communauté pour seulement 13,60 € après déduction fiscale.</p>
            </div>
            <div class="mt-8">
              <router-link
                to="/public-subscribe"
                class="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-medium rounded-xl shadow-sm text-white bg-zebu-orange hover:bg-zebu-orange/90 transition-all duration-300"
              >
                S'inscrire
                <svg class="ml-2 w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                </svg>
              </router-link>
            </div>
          </div>
        </div>

        <!-- Carte Financement -->
        <div class="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
          <div class="p-8 flex flex-col h-full">
            <div class="w-16 h-16 bg-zebu-orange/10 rounded-xl flex items-center justify-center mb-6">
              <svg class="w-8 h-8 text-zebu-orange" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="flex-grow">
              <h3 class="text-2xl font-semibold text-gray-900 mb-4">Financer un projet</h3>
              <p class="text-lg text-gray-600">Participez à l'amélioration des conditions de vies des populations vulnérables dans les pays du Sud.</p>
            </div>
            <div class="mt-8">
              <a
                :href="`${mainUrl}/subscribe`"
                class="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-medium rounded-xl shadow-sm text-white bg-zebu-orange hover:bg-zebu-orange/90 transition-all duration-300"
              >
                Découvrir
                <svg class="ml-2 w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <!-- Carte Don -->
        <div class="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
          <div class="p-8 flex flex-col h-full">
            <div class="w-16 h-16 bg-zebu-orange/10 rounded-xl flex items-center justify-center mb-6">
              <svg class="w-8 h-8 text-zebu-orange" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <div class="flex-grow">
              <h3 class="text-2xl font-semibold text-gray-900 mb-4">Faire un don</h3>
              <p class="text-lg text-gray-600">Soutenez nos actions avec un don déductible des impôts.</p>
            </div>
            <div class="mt-8">
              <a
                href="/donate"
                class="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-medium rounded-xl shadow-sm text-white bg-zebu-orange hover:bg-zebu-orange/90 transition-all duration-300"
              >
                Faire un don
                <svg class="ml-2 w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <!-- Carte Cadeau -->
        <div class="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
          <div class="p-8 flex flex-col h-full">
            <div class="w-16 h-16 bg-zebu-orange/10 rounded-xl flex items-center justify-center mb-6">
              <svg class="w-8 h-8 text-zebu-orange" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
            </div>
            <div class="flex-grow">
              <h3 class="text-2xl font-semibold text-gray-900 mb-4">Faire un cadeau</h3>
              <p class="text-lg text-gray-600">Par l'intermédiaire de Zebunet, financez des parrainages, au nom de vos proches !</p>
            </div>
            <div class="mt-8">
              <router-link
                to="/gift"
                class="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-medium rounded-xl shadow-sm text-white bg-zebu-orange hover:bg-zebu-orange/90 transition-all duration-300"
              >
                Offrir un cadeau
                <svg class="ml-2 w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                </svg>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- État connecté (contenu existant) -->
    <div v-else>
      <!-- Loading state -->
      <div v-if="loading" class="flex flex-col items-center justify-center min-h-[60vh]">
        <div class="animate-spin rounded-full h-12 w-12 border-4 border-zebu-orange border-b-transparent"></div>
        <p class="mt-4 text-gray-600 text-lg">Chargement du tableau de bord...</p>
      </div>

      <div v-else class="space-y-6">
        <!-- En-tête avec titre et alerte -->
        <div class="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 class="text-2xl font-semibold text-gray-900">Tableau de bord</h1>
          
          <!-- Alerte de renouvellement si nécessaire -->
          <div v-if="shouldShowRenewalAlert" 
            class="w-full sm:w-auto bg-amber-50 border-l-4 border-amber-400 p-4 rounded-lg shadow-sm"
          >
            <div class="flex items-start space-x-3">
              <div class="flex-shrink-0">
                <svg class="h-6 w-6 text-amber-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd" />
                </svg>
              </div>
              <div>
                <p class="text-sm text-amber-800">
                  Votre adhésion expire dans <span class="font-semibold">{{ dashboardData.subscription_status.days_until_expiration }} jours</span>
                </p>
                <router-link 
                  to="/subscribe" 
                  class="mt-1 inline-flex items-center text-sm text-amber-700 hover:text-amber-900 font-medium"
                >
                  Renouveler mon adhésion
                  <svg class="ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Grille principale -->
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <!-- Colonne de gauche -->
          <div class="lg:col-span-8 space-y-6">
            <!-- Carte d'adhésion -->
            <div class="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
              <div class="p-6 border-b border-gray-100">
                <h2 class="text-lg font-semibold text-gray-900">Statut de l'adhésion</h2>
              </div>
              
              <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <!-- Informations principales -->
                  <div class="space-y-6">
                    <div class="bg-gray-50 rounded-lg p-4">
                      <p class="text-sm text-gray-600 mb-1">Date d'adhésion</p>
                      <p class="text-base font-medium text-gray-900">{{ formatDateWithTime(dashboardData.subscribed_on) }}</p>
                    </div>
                    <div class="bg-gray-50 rounded-lg p-4">
                      <p class="text-sm text-gray-600 mb-1">Adhésion valable jusqu'au</p>
                      <p class="text-base font-medium text-gray-900">{{ formatDate(dashboardData.subscription_end_date) }}</p>
                    </div>
                    <div class="bg-gray-50 rounded-lg p-4">
                      <p class="text-sm text-gray-600 mb-1">Statut de l'adhésion</p>
                      <div class="flex items-center mt-1">
                        <div :class="[
                          'px-3 py-1 rounded-full text-sm font-medium inline-flex items-center',
                          dashboardData.subscription_status.is_active === 1 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        ]">
                          <span class="relative flex h-2 w-2 mr-2">
                            <span :class="[
                              'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
                              dashboardData.subscription_status.is_active === 1 ? 'bg-green-400' : 'bg-red-400'
                            ]"></span>
                            <span :class="[
                              'relative inline-flex rounded-full h-2 w-2',
                              dashboardData.subscription_status.is_active === 1 ? 'bg-green-500' : 'bg-red-500'
                            ]"></span>
                          </span>
                          {{ dashboardData.subscription_status.is_active === 1 ? 'Active' : 'Expirée' }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Statistiques et actions -->
                  <div class="space-y-6">
                    <FundingStats class="bg-gray-50 rounded-lg p-4" />
                    
                    <router-link 
                      to="/my-herd"
                      class="block bg-white border-2 border-zebu-orange text-zebu-orange hover:bg-zebu-orange hover:text-white rounded-lg p-4 transition-colors duration-300"
                    >
                      <div class="flex justify-between items-center">
                        <span class="text-sm font-medium">Voir mon cheptel</span>
                        <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                        </svg>
                      </div>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section des factures - COMMENTÉE POUR UNE UTILISATION FUTURE -->
            <!-- 
            <div class="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
              <div class="p-6 border-b border-gray-100">
                <h2 class="text-lg font-semibold text-gray-900">Historique des contributions</h2>
              </div>
              <div class="p-6">
                <div v-if="!loading">
                  <div v-if="invoices?.length > 0">
                    <div v-if="invoices.length > 10" class="mb-4 flex justify-end">
                      <div class="relative">
                        <select
                          v-model="currentPage"
                          class="appearance-none bg-white border border-gray-300 rounded-lg py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-zebu-orange focus:border-zebu-orange"
                        >
                          <option
                            v-for="page in totalPages"
                            :key="page"
                            :value="page"
                          >
                            Page {{ page }} sur {{ totalPages }}
                          </option>
                        </select>
                        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div class="space-y-4">
                      <div
                        v-for="invoice in paginatedInvoices"
                        :key="invoice.id"
                        class="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors duration-200"
                      >
                        <div class="flex flex-col space-y-3">
                          <div class="flex justify-between items-start">
                            <div>
                              <p class="font-medium text-gray-900">
                                {{ formatAmount(invoice.amount || 0) }}
                                <span v-if="invoice.payment_method" class="text-sm text-gray-500 ml-2">({{ invoice.payment_method }})</span>
                              </p>
                              <p v-if="invoice.received_date" class="text-sm text-gray-600">
                                Reçue le {{ formatDateWithTime(invoice.received_date) }}
                              </p>
                            </div>
                            <div class="flex flex-col items-end">
                              <span v-if="invoice.status" :class="[
                                'px-2 py-1 rounded-full text-xs font-medium',
                                invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                                invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              ]">
                                {{ formatStatus(invoice.status) }}
                              </span>
                              <p v-if="invoice.payment_type" class="text-xs text-gray-500 mt-1">{{ invoice.payment_type }}</p>
                            </div>
                          </div>

                          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p v-if="invoice.issued_date" class="text-gray-600">Émise le: {{ formatDate(invoice.issued_date) }}</p>
                              <p v-if="invoice.created_date" class="text-gray-600">Créée le: {{ formatDate(invoice.created_date) }}</p>
                              <p class="text-gray-600">Réf. transaction: {{ invoice.transaction_reference || 'N/A' }}</p>
                            </div>
                            <div>
                              <p class="text-gray-600">Réf. paiement: {{ invoice.payment_reference || 'N/A' }}</p>
                              <p class="text-gray-600">Session Stripe: {{ invoice.stripe_session_id || 'N/A' }}</p>
                              <p class="text-gray-600">Paiement Stripe: {{ invoice.stripe_payment_id || 'N/A' }}</p>
                            </div>
                          </div>

                          <div v-if="invoice.comments" class="text-sm text-gray-600 bg-gray-100 p-3 rounded">
                            <p class="italic">{{ invoice.comments }}</p>
                          </div>

                          <div class="flex justify-end space-x-3">
                            <a
                              v-if="invoice.stripe_invoice_pdf"
                              :href="invoice.stripe_invoice_pdf"
                              target="_blank"
                              class="inline-flex items-center text-sm text-zebu-orange hover:text-zebu-orange-dark"
                            >
                              Télécharger PDF
                              <svg class="ml-1 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
                              </svg>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="invoices.length > 10" class="mt-4 sm:hidden">
                      <div class="flex justify-between items-center">
                        <button
                          @click="previousPage"
                          :disabled="currentPage === 1"
                          class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Précédent
                        </button>
                        <span class="text-sm text-gray-700">
                          Page {{ currentPage }} sur {{ totalPages }}
                        </span>
                        <button
                          @click="nextPage"
                          :disabled="currentPage === totalPages"
                          class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Suivant
                        </button>
                      </div>
                    </div>
                  </div>

                  <div v-else class="text-center py-6">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900">Aucune contribution</h3>
                    <p class="mt-1 text-sm text-gray-500">
                      Vous n'avez pas encore effectué de contribution.
                    </p>
                  </div>
                </div>

                <div v-else class="flex justify-center py-8">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-zebu-orange"></div>
                </div>
              </div>
            </div>
            -->
          </div>

          <!-- Colonne de droite -->
          <div class="lg:col-span-4 space-y-6">
            <!-- Actions rapides -->
            <div class="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
              <div class="p-6 border-b border-gray-100">
                <h2 class="text-lg font-semibold text-gray-900">Actions rapides</h2>
              </div>
              <div class="p-6 space-y-4">
                <router-link 
                  to="/subscribe"
                  class="block w-full bg-zebu-orange text-zebu-yellow hover:bg-opacity-90 rounded-lg p-4 transition-colors duration-300 shadow-sm hover:shadow-md"
                >
                  <div class="flex justify-between items-center">
                    <span class="text-sm font-medium">Financer un projet</span>
                    <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                    </svg>
                  </div>
                </router-link>

                <router-link 
                  to="/account"
                  class="block w-full bg-white border-2 border-gray-200 hover:border-zebu-orange text-gray-700 hover:text-zebu-orange rounded-lg p-4 transition-colors duration-300"
                >
                  <div class="flex justify-between items-center">
                    <span class="text-sm font-medium">Gérer mon compte</span>
                    <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                    </svg>
                  </div>
                </router-link>
              </div>
            </div>

            <!-- Section d'adhésion -->
            <div class="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
              <div class="p-6 border-b border-gray-100">
                <h2 class="text-lg font-semibold text-gray-900">Adhésion Zebunet</h2>
              </div>
              
              <div class="p-6">
                <div v-if="dashboardData.subscription_status.is_active === 1" 
                  class="bg-gray-50 rounded-lg p-6"
                >
                  <div class="space-y-4">
                    <h3 class="text-lg font-medium text-gray-900">Renouveler mon adhésion</h3>
                    <p class="text-gray-600">
                      En renouvelant votre adhésion maintenant, vous continuez à soutenir nos actions pour seulement 
                      <span class="font-medium text-lg">13,60 €</span> après déduction fiscale de 66% 
                      (cotisation annuelle de 40 €).
                    </p>
                    <button
                      v-if="shouldShowRenewalButton"
                      @click="handleRenewal"
                      :disabled="isProcessingPayment"
                      class="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-zebu-orange hover:bg-zebu-orange/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg v-if="isProcessingPayment" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {{ isProcessingPayment ? 'Traitement en cours...' : 'Adhérer maintenant' }}
                    </button>
                    <button
                      v-else
                      disabled
                      class="w-full inline-flex items-center justify-center px-6 py-3 border border-gray-200 text-base font-medium rounded-lg shadow-sm text-gray-400 bg-gray-50 cursor-not-allowed"
                    >
                      Ré-adhésion possible dans {{ daysUntilRenewal }} jours
                    </button>
                  </div>
                </div>

                <div v-else class="bg-amber-50 rounded-lg p-6">
                  <div class="space-y-4">
                    <h3 class="text-lg font-medium text-gray-900">Devenir membre</h3>
                    <p class="text-gray-600">
                      Devenez membre pour seulement <span class="font-medium text-lg">13,60 €</span> après déduction fiscale de 66% 
                      (cotisation annuelle de 40 €).
                    </p>
                    <button
                      @click="handleRenewal"
                      :disabled="isProcessingPayment"
                      class="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-zebu-orange hover:bg-zebu-orange/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg v-if="isProcessingPayment" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {{ isProcessingPayment ? 'Traitement en cours...' : 'Adhérer maintenant' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { memberareaService } from '@/services/memberarea.service';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import FundingStats from '@/components/dashboard/FundingStats.vue';
// import InvoicesList from '@/components/dashboard/InvoicesList.vue'; // Commenté car non utilisé pour le moment
import axios from 'axios';
import { useToast } from 'vue-toastification';

const toast = useToast();
const loading = ref(true);
const isProcessingPayment = ref(false);
// Commenté pour une utilisation future
/*
const invoices = ref([]);
const currentPage = ref(1);
const itemsPerPage = 10;
*/

const dashboardData = ref({
  user: null,
  funded_plans: 0,
  subscription_end_date: null,
  subscribed_on: null,
  subscription_status: {
    is_active: 0,
    days_until_expiration: 0
  }
});
const router = useRouter();
const authStore = useAuthStore();
const isAuthenticated = computed(() => authStore.isAuthenticated);
const mainUrl = import.meta.env.VITE_MAIN_URL;

// Commenté pour une utilisation future
/*
// Calcul des pages pour la pagination
const totalPages = computed(() => {
  return Math.ceil((invoices.value?.length || 0) / itemsPerPage);
});

const paginatedInvoices = computed(() => {
  if (!invoices.value?.length) return [];
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return invoices.value.slice(start, end);
});

// Navigation pagination
const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

// Formatage du statut
const formatStatus = (status) => {
  const statusMap = {
    'paid': 'Payée',
    'pending': 'En attente',
    'failed': 'Échouée',
    'cancelled': 'Annulée'
  };
  return statusMap[status] || status;
};

// Chargement des factures
const loadInvoices = async () => {
  try {
    const response = await memberareaService.getInvoices();
    if (response.success) {
      invoices.value = response.data || [];
    }
  } catch (error) {
    console.error('Erreur lors du chargement des factures:', error);
    toast.error('Erreur lors du chargement de l\'historique des contributions');
    invoices.value = [];
  }
};
*/

// Modification du template pour utiliser v-if avec une vérification plus sûre
onMounted(async () => {
  try {
    loading.value = true;
    // Commenté pour ne charger que les données du dashboard sans l'historique des factures
    /*
    await Promise.all([
      (async () => {
        const dashboardResponse = await memberareaService.getDashboardData();
        if (dashboardResponse.success) {
          dashboardData.value = dashboardResponse;
        }
      })(),
      loadInvoices()
    ]);
    */
    
    // Chargement uniquement des données du dashboard
    const dashboardResponse = await memberareaService.getDashboardData();
    if (dashboardResponse.success) {
      dashboardData.value = dashboardResponse;
    }
    
  } catch (error) {
    console.error('Erreur lors du chargement des données:', error);
  } finally {
    loading.value = false;
  }
});

// Formater la date en format français
const formatDate = (dateString) => {
  if (!dateString) return 'Non disponible';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date invalide';
    
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  } catch (error) {
    console.error('Erreur de formatage de date:', error);
    return 'Date invalide';
  }
};

// Formater la date en format français avec heure
const formatDateWithTime = (dateString) => {
  if (!dateString) return 'Non disponible';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date invalide';
    
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  } catch (error) {
    console.error('Erreur de formatage de date:', error);
    return 'Date invalide';
  }
};

// Déterminer si on doit afficher l'alerte de renouvellement (30 jours avant expiration)
const shouldShowRenewalAlert = computed(() => {
  const daysLeft = dashboardData.value.subscription_status.days_until_expiration;
  return daysLeft <= 30 && daysLeft > 0;
});

// Calculer le nombre de jours avant de pouvoir renouveler
const daysUntilRenewal = computed(() => {
  const daysLeft = dashboardData.value.subscription_status.days_until_expiration;
  return Math.max(daysLeft - 30, 0); // Si plus de 30 jours, afficher le nombre de jours restants avant de pouvoir renouveler
});

// Déterminer si on doit afficher le bouton de renouvellement
const shouldShowRenewalButton = computed(() => {
  const daysLeft = dashboardData.value.subscription_status.days_until_expiration;
  return daysLeft <= 30; // Afficher le bouton actif seulement si moins de 30 jours avant expiration
});

// Gestion du renouvellement d'adhésion
const handleRenewal = async () => {
  if (isProcessingPayment.value) return;
  
  isProcessingPayment.value = true;
  try {
    console.log('Début de la procédure de paiement...');
    const response = await memberareaService.createMembershipPayment();
    
    console.log('Réponse du serveur:', response);
    
    if (response.success && response.url) {
      console.log('Redirection vers:', response.url);
      window.location.href = response.url;
    } else {
      toast.error("Une erreur est survenue lors de l'initialisation du paiement");
    }
  } catch (error) {
    console.error('Erreur détaillée:', error);
    toast.error(error.response?.data?.message || "Une erreur est survenue lors de l'initialisation du paiement");
  } finally {
    isProcessingPayment.value = false;
  }
}

// Ajout de la fonction pour formater le montant - commenté car non utilisé pour le moment
/*
const formatAmount = (amount) => {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
};
*/
</script>

<style scoped>
/* Animation du point de statut */
@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

/* Ajout de styles responsives supplémentaires */
@media (max-width: 640px) {
  .grid {
    gap: 1rem;
  }
}
</style> 